import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Stack,
    Typography,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { getLectureList } from './LectureApi';

// const SERVER = process.env.REACT_APP_SERVER;

const colors = {
    text: '#1a1a1a',
    primary: '#005a8d',
    primaryDark: '#004870',
    primaryLight: '#f5f9fc',
    border: '#e7edf2',
    muted: '#555555',
};

const normalizeLectures = (payload) => {
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
};

const parseDate = (value) => {
    if (!value) return null;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    date.setHours(0, 0, 0, 0);
    return date;
};

const getRegistrationStatus = (regDateStart, regDateEnd) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = parseDate(regDateStart);
    const end = parseDate(regDateEnd);

    if (!start || !end) {
        return { label: '일정 확인', color: 'default', tone: '#64748b' };
    }

    if (today < start) {
        return { label: '접수 예정', color: 'info', tone: '#0f6f94' };
    }

    if (today > end) {
        return { label: '접수 마감', color: 'default', tone: '#6b7280' };
    }

    return { label: '접수 중', color: 'success', tone: '#0f7a43' };
};
const getLectureStatus = (lecDateStart, lecDateEnd, isActivated) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = parseDate(lecDateStart);
    const end = parseDate(lecDateEnd);

    if (isActivated !== 1 || (end && today > end)) {
        return { label: '수강종료', color: 'default', variant: 'outlined' };
    }

    if (start && today < start) {
        return { label: '수강예정', color: 'info', variant: 'outlined' };
    }

    if (!start || !end) {
        return { label: '일정 확인', color: 'default', variant: 'outlined' };
    }

    return { label: '수강중', color: 'primary', variant: 'filled' };
};

const formatDateString = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}.${m}.${d}`;
};

const formatPeriod = (start, end) => {
    const s = formatDateString(start);
    const e = formatDateString(end);
    
    if (!s && !e) return '추후 안내';
    if (!s) return `~ ${e}`;
    if (!e) return `${s} ~`;
    return `${s} ~ ${e}`;
};

function LecturePage() {
    const navigate = useNavigate();
    const [lectureList, setLectureList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let mounted = true;

        const fetchLectures = async () => {
            try {
                const res = await getLectureList();
                let data = normalizeLectures(res.data);
                
                if (mounted) {
                    setLectureList([...data].reverse());
                    setError('');
                }
            } catch (err) {
                console.error('API 호출 실패 상세 원인:', err);
                if (mounted) {
                    setError('교육 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        fetchLectures();

        return () => {
            mounted = false;
        };
    }, []);

    const lectureCountText = useMemo(() => {
        if (loading) return '교육 과정을 확인하고 있습니다';
        return `${lectureList.length}개의 교육 과정`;
    }, [lectureList.length, loading]);

    const handleClick = (id) => {
        if (!id) return;
        navigate(`/lecture/${id}`);
    };

    return (
        <Box sx={{ bgcolor: '#ffffff', color: colors.text, minHeight: '100vh' }}>
            

            <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#ffffff' }}>
                <Container maxWidth="lg">
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
                        spacing={2}
                        sx={{ mb: { xs: 4, md: 6 } }}
                    >
                        <Box>
                            <Typography
                                variant="h4"
                                sx={{ fontWeight: 800, mb: 1.5, letterSpacing: '-0.02em' }}
                            >
                                진행 중인 교육
                            </Typography>
                            <Typography sx={{ color: colors.muted, wordBreak: 'keep-all' }}>
                                접수 기간과 담당 강사를 확인한 뒤 원하는 과정을 선택해 주세요.
                            </Typography>
                        </Box>
                        <Chip
                            label={lectureCountText}
                            sx={{
                                bgcolor: colors.primaryLight,
                                color: colors.primaryDark,
                                fontWeight: 700,
                                px: 1,
                            }}
                        />
                    </Stack>

                    {loading && (
                        <Box
                            sx={{
                                minHeight: 260,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Stack alignItems="center" spacing={2}>
                                <CircularProgress sx={{ color: colors.primary }} />
                                <Typography sx={{ color: colors.muted }}>
                                    교육 목록을 불러오고 있습니다.
                                </Typography>
                            </Stack>
                        </Box>
                    )}

                    {!loading && error && (
                        <Alert severity="error" sx={{ borderRadius: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {!loading && !error && lectureList.length === 0 && (
                        <Box
                            sx={{
                                border: `1px solid ${colors.border}`,
                                borderRadius: 4,
                                bgcolor: colors.primaryLight,
                                py: { xs: 8, md: 10 },
                                px: 3,
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                                현재 공개된 교육 과정이 없습니다.
                            </Typography>
                            <Typography sx={{ color: colors.muted }}>
                                새로운 일정이 확정되면 이곳에서 안내드리겠습니다.
                            </Typography>
                        </Box>
                    )}

                    {!loading && !error && lectureList.length > 0 && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr',
                                gap: 3,
                            }}
                        >
                            {lectureList.map((item) => {
                                const status = getRegistrationStatus(
                                    item.reg_period_start,
                                    item.reg_period_end
                                );
                                const lectureStatus = getLectureStatus(
                                    item.lec_period_start,
                                    item.lec_period_end,
                                    item.is_activated
                                );

                                return (
                                    <Box
                                        key={item.id}
                                        component="article"
                                        onClick={() => handleClick(item.id)}
                                        sx={{
                                            border: `1px solid ${colors.border}`,
                                            borderRadius: 4,
                                            overflow: 'hidden',
                                            minHeight: 260,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            cursor: item.id ? 'pointer' : 'default',
                                            bgcolor: '#ffffff',
                                            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.04)',
                                            transition:
                                                'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                                            '&:hover': item.id
                                                ? {
                                                      transform: 'translateY(-6px)',
                                                      borderColor: 'rgba(0, 90, 141, 0.35)',
                                                      boxShadow:
                                                          '0 18px 34px rgba(15, 23, 42, 0.08)',
                                                  }
                                                : undefined,
                                        }}
                                    >
                                

                                        {/* 상세 정보 영역 */}
                                        <Box sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'space-between' }}>
                                            <Box>
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                spacing={2}
                                                sx={{ mb: 3 }}
                                            >
                                                <Stack direction="row" spacing={1}>
                                                    <Chip
                                                        label={status.label}
                                                        color={status.color}
                                                        size="small"
                                                        sx={{ fontWeight: 800 }}
                                                    />
                                                    <Chip
                                                        label={lectureStatus.label}
                                                        color={lectureStatus.color}
                                                        variant={lectureStatus.variant}
                                                        size="small"
                                                        sx={{ fontWeight: 800 }}
                                                    />
                                                </Stack>
                                               
                                            </Stack>

                                            <Typography
                                                component="h2"
                                                sx={{
                                                    fontSize: { xs: '1.35rem', md: '1.5rem' },
                                                    fontWeight: 800,
                                                    lineHeight: 1.35,
                                                    wordBreak: 'keep-all',
                                                    mb: 2.5,
                                                }}
                                            >
                                                {item.title || '제목 미정'}
                                            </Typography>

                                            <Stack spacing={1.5}>
                                                {/* 주최/주관 */}
                                                {item.organizer && (
                                                    <Stack direction="row" spacing={1.25} alignItems="center">
                                                        <BusinessOutlinedIcon fontSize="small" sx={{ color: colors.primary }} />
                                                        <Typography sx={{ color: colors.muted, fontSize: '0.9rem' }}>
                                                            {item.organizer}
                                                        </Typography>
                                                    </Stack>
                                                )}
                                                
                                                {/* 강사 */}
                                                <Stack
                                                    direction="row"
                                                    spacing={1.25}
                                                    alignItems="center"
                                                >
                                                    <PersonOutlineOutlinedIcon
                                                        fontSize="small"
                                                        sx={{ color: colors.primary }}
                                                    />
                                                    <Typography sx={{ color: colors.muted, fontSize: '0.9rem' }}>
                                                        {item.instructor_name || '강사 추후 안내'}
                                                    </Typography>
                                                </Stack>

                                                {/* 교육 기간 */}
                                                <Stack
                                                    direction="row"
                                                    spacing={1.25}
                                                    alignItems="center"
                                                >
                                                    <EventAvailableOutlinedIcon
                                                        fontSize="small"
                                                        sx={{ color: colors.primary }}
                                                    />
                                                    <Typography sx={{ color: colors.muted, fontSize: '0.9rem' }}>
                                                        <Box component="span" sx={{ fontWeight: 600, mr: 1, color: colors.text }}>교육 기간</Box>
                                                        {formatPeriod(item.lec_period_start, item.lec_period_end)}
                                                    </Typography>
                                                </Stack>

                                                {/* 접수 기간 */}
                                                <Stack direction="row" spacing={1.25} alignItems="center">
                                                    <CalendarMonthOutlinedIcon
                                                        fontSize="small"
                                                        sx={{ color: colors.primary }}
                                                    />
                                                    <Typography sx={{ color: colors.muted, fontSize: '0.9rem' }}>
                                                        <Box component="span" sx={{ fontWeight: 600, mr: 1, color: colors.text }}>접수 기간</Box>
                                                        {formatPeriod(
                                                            item.reg_period_start,
                                                            item.reg_period_end
                                                        )}
                                                    </Typography>
                                                </Stack>
                                                </Stack>
                                            </Box>

                                        <Button
                                            variant="text"
                                            endIcon={<ArrowForwardIcon />}
                                            sx={{
                                                alignSelf: 'flex-start',
                                                mt: 4,
                                                px: 0,
                                                color: colors.primary,
                                                fontWeight: 800,
                                                '&:hover': {
                                                    bgcolor: 'transparent',
                                                    color: colors.primaryDark,
                                                },
                                            }}
                                        >
                                            자세히 보기
                                        </Button>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Container>
            </Box>
        </Box>
    );
}

export default LecturePage;
